import React from 'react'
import styles from './IntegrationNotice.module.scss'

interface IntegrationNoticeProps {
  /** Name of the third-party service the section depends on, e.g. "TaxJar". */
  service: string
  title?: string
  message?: string
}

/**
 * Friendly placeholder for admin sections whose data comes from a third-party
 * integration that is not configured on this environment. Rendered instead of
 * an endless spinner or an empty table so the section reads as "pending", not
 * "broken".
 */
const IntegrationNotice = ({service, title, message}: IntegrationNoticeProps) => (
  <div className={styles.container} role="status">
    <span className={styles.badge}>Integration pending</span>
    <h2 className={styles.title}>
      {title ?? `${service} is not connected on this environment yet`}
    </h2>
    <p className={styles.message}>
      {message ??
        `This section is powered by ${service}. It will start showing data once the ${service} integration has been configured for this environment.`}
    </p>
  </div>
)

/** Toast text used by every tax-related section when TaxJar is unavailable. */
export const TAXJAR_NOT_INTEGRATED_TOAST = 'TaxJar Not Integrated'

/**
 * Shared placeholder for the tax sections. Deliberately generic: TaxJar is the
 * US state-level sales tax service behind these screens, and the client does
 * not need to know the vendor name to understand why the section is empty.
 */
export const SalesTaxNotIntegratedNotice = () => (
  <IntegrationNotice
    service="TaxJar"
    title="State-level sales tax system is not integrated yet"
    message="Sales tax calculation, exemption certificates and customer tax profiles are handled by a third-party state sales tax service that has not been connected to this environment. This section will populate once that integration is configured."
  />
)

export default IntegrationNotice
